<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import Icon from 'dashboard/components-next/icon/Icon.vue';

const props = defineProps({
  kind: {
    type: String,
    required: true,
    validator: value => ['choice', 'form', 'rate', 'pay'].includes(value),
  },
  title: { type: String, default: '' },
  subtitle: { type: String, default: '' },
  options: { type: Array, default: () => [] },
  payAmount: { type: String, default: '' },
  payHref: { type: String, default: '' },
});

const { t } = useI18n();

const theme = computed(() => {
  if (props.kind === 'rate') {
    return {
      kicker: t('CONVERSATION.REPLYBOX.USER_ACTIONS.CARD_KICKER_RATE'),
      icon: 'i-ph-star-fill',
      iconWrap: 'bg-n-amber-9/20',
      accent: 'text-n-amber-9',
    };
  }
  if (props.kind === 'pay') {
    return {
      kicker: t('CONVERSATION.REPLYBOX.USER_ACTIONS.CARD_KICKER_PAY'),
      icon: 'i-ph-credit-card',
      iconWrap: 'bg-n-iris-9/20',
      accent: 'text-n-iris-9',
    };
  }
  if (props.kind === 'form') {
    return {
      kicker: t('CONVERSATION.REPLYBOX.USER_ACTIONS.CARD_KICKER_FORM'),
      icon: 'i-ph-clipboard-text',
      iconWrap: 'bg-n-blue-9/15',
      accent: 'text-n-blue-9',
    };
  }
  return {
    kicker: t('CONVERSATION.REPLYBOX.USER_ACTIONS.CARD_KICKER_CHOICE'),
    icon: 'i-ph-squares-four',
    iconWrap: 'bg-n-blue-9/15',
    accent: 'text-n-blue-9',
  };
});

const heading = computed(() => {
  const raw = String(props.title || '')
    .replace(/<[^>]+>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  if (raw) return raw;
  if (props.kind === 'rate') {
    return t('CONVERSATION.REPLYBOX.USER_ACTIONS.CSAT_DEFAULT');
  }
  return '';
});

const payLabel = computed(() =>
  props.payAmount
    ? t('CONVERSATION.REPLYBOX.USER_ACTIONS.PAY_CTA', {
        amount: props.payAmount,
      })
    : t('CONVERSATION.REPLYBOX.USER_ACTIONS.PAY_NOW')
);
</script>

<template>
  <div data-action-card class="w-full min-w-0 rounded-xl bg-n-slate-5 p-2.5">
    <div class="flex shrink-0 items-start gap-2.5">
      <span
        class="flex size-7 shrink-0 items-center justify-center rounded-[var(--workspace-radius,10px)]"
        :class="theme.iconWrap"
      >
        <Icon :icon="theme.icon" class="size-3.5" :class="theme.accent" />
      </span>
      <span class="flex min-w-0 flex-1 flex-col">
        <span
          class="text-[10px] font-bold uppercase tracking-[0.12em]"
          :class="theme.accent"
        >
          {{ theme.kicker }}
        </span>
        <span
          v-if="heading"
          class="text-[13px] font-bold leading-tight tracking-tight text-n-slate-12"
        >
          {{ heading }}
        </span>
        <span
          v-if="subtitle"
          class="mt-0.5 text-xs leading-snug text-n-slate-11"
        >
          {{ subtitle }}
        </span>
      </span>
    </div>

    <div v-if="kind === 'rate'" class="mt-3 flex gap-1.5" aria-hidden="true">
      <span
        v-for="n in 5"
        :key="n"
        class="i-ph-star-fill size-5 shrink-0 text-n-amber-9"
      />
    </div>

    <div v-else-if="options.length" class="mt-2.5 flex flex-col gap-1.5">
      <div
        v-for="option in options"
        :key="option"
        class="rounded-[var(--workspace-radius,11px)] bg-n-slate-6 px-3 py-2 text-[13px] font-bold text-n-slate-12"
      >
        {{ option }}
      </div>
    </div>

    <a
      v-if="kind === 'pay' && payHref"
      :href="payHref"
      target="_blank"
      rel="noopener noreferrer"
      class="mt-2.5 block rounded-xl bg-n-iris-9 px-3 py-2.5 text-center text-[13px] font-medium text-white no-underline"
    >
      {{ payLabel }}
    </a>
    <div
      v-else-if="kind === 'pay'"
      class="mt-2.5 rounded-xl bg-n-iris-9 px-3 py-2.5 text-center text-[13px] font-medium text-white"
    >
      {{ payLabel }}
    </div>
  </div>
</template>
